/**
 * Frontend Logging Utility
 * Provides structured logging for API calls, route navigation, and debugging
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: any;
}

class Logger {
  private enabled: boolean = true;
  private logLevel: LogLevel = 'debug';
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000;

  constructor() {
    // Check if logging is disabled via localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('frontend_logging_enabled');
      if (stored === 'false') {
        this.enabled = false;
      }
      
      const level = localStorage.getItem('frontend_log_level');
      if (level && ['info', 'warn', 'error', 'debug'].includes(level)) {
        this.logLevel = level as LogLevel;
      }
    }
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.enabled) return false;
    
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    return levels.indexOf(level) >= levels.indexOf(this.logLevel);
  }

  private formatTimestamp(): string {
    return new Date().toISOString();
  }

  private addLog(level: LogLevel, category: string, message: string, data?: any): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      timestamp: this.formatTimestamp(),
      level,
      category,
      message,
      data,
    };

    // Store in memory (limited)
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Format for console
    const style = this.getStyle(level);
    const prefix = `%c[${entry.timestamp}] [${category.toUpperCase()}]`;
    const logMessage = `${prefix} ${message}`;

    if (data) {
      console.log(logMessage, style, data);
    } else {
      console.log(logMessage, style);
    }
  }

  private getStyle(level: LogLevel): string {
    const styles: Record<LogLevel, string> = {
      debug: 'color: #6b7280; font-weight: normal;',
      info: 'color: #3b82f6; font-weight: normal;',
      warn: 'color: #f59e0b; font-weight: bold;',
      error: 'color: #ef4444; font-weight: bold;',
    };
    return styles[level] || '';
  }

  // Public API
  api(method: string, url: string, data?: any, response?: any, error?: any): void {
    if (error) {
      this.addLog('error', 'API', `${method} ${url}`, {
        error: error.message || error,
        status: error.status || error.response?.status,
        data: error.data || error.response?.data,
        requestData: data,
      });
    // } else if (response) {
    //   this.addLog('info', 'API', `${method} ${url}`, {
    //     status: response.status || 'success',
    //     responseTime: response.responseTime,
    //     data: response.data,
    //     requestData: data,
    //   });
    } 
    // else {
    //   this.addLog('debug', 'API', `${method} ${url}`, {
    //     requestData: data,
    //     headers: this.getRequestHeaders(),
    //   });
    // }
  }

  route(pathname: string, search?: string, state?: any): void {
    this.addLog('info', 'ROUTE', `Navigation to: ${pathname}${search || ''}`, {
      fullPath: pathname + (search || ''),
      state,
      previousPath: this.getPreviousPath(),
    });
  }

  error(category: string, message: string, error?: any): void {
    this.addLog('error', category, message, {
      error: error?.message || error,
      stack: error?.stack,
      ...error,
    });
  }

  warn(category: string, message: string, data?: any): void {
    this.addLog('warn', category, message, data);
  }

  info(category: string, message: string, data?: any): void {
    this.addLog('info', category, message, data);
  }

  debug(category: string, message: string, data?: any): void {
    this.addLog('debug', category, message, data);
  }

  // Utility methods
  private getRequestHeaders(): Record<string, string> {
    if (typeof window === 'undefined') return {};
    
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = 'Bearer [REDACTED]';
    }
    
    return headers;
  }

  private getPreviousPath(): string {
    return sessionStorage.getItem('previous_path') || 'unknown';
  }

  // Get all logs (for debugging)
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Clear logs
  clearLogs(): void {
    this.logs = [];
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Enable/disable logging
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (typeof window !== 'undefined') {
      localStorage.setItem('frontend_logging_enabled', String(enabled));
    }
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    if (typeof window !== 'undefined') {
      localStorage.setItem('frontend_log_level', level);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Make logger available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).logger = logger;
  (window as any).exportLogs = () => {
    const logs = logger.exportLogs();
    const blob = new Blob([logs], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frontend-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  console.log('%cFrontend Logger Initialized', 'color: #10b981; font-weight: bold; font-size: 14px;');
  console.log('%cAvailable commands:', 'color: #3b82f6; font-weight: bold;');
  console.log('  - logger.getLogs() - Get all logs');
  console.log('  - logger.clearLogs() - Clear logs');
  console.log('  - logger.exportLogs() - Export logs as JSON');
  console.log('  - logger.setEnabled(true/false) - Enable/disable logging');
  console.log('  - logger.setLogLevel("debug"|"info"|"warn"|"error") - Set log level');
  console.log('  - exportLogs() - Download logs as JSON file');
}






